import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Step,
  Stepper,
  StepLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarTodayIcon,
  LocationOn as LocationOnIcon,
  Folder as FolderIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Edit as EditIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { storage, db } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

function MembershipApplication() {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [formData, setFormData] = useState({
    name: '',
    residentialAddress: '',
    officeAddress: '',
    mobile: '',
    email: '',
    barCouncilId: '',
    photo: null,
    barCouncilCertificate: null,
    cop: null,
    paymentProof: null
  });

  const [enrollmentYear, setEnrollmentYear] = useState(null);
  const [showCOP, setShowCOP] = useState(false);

  const [errors, setErrors] = useState({});

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const MAX_FILE_SIZE = 300 * 1024; // 300KB in bytes

  const steps = ['Personal Information', 'Documents Upload', 'Review & Submit'];

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateMobile = (mobile) => {
    return /^[0-9]{10}$/.test(mobile);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for mobile number
    if (name === 'mobile') {
      // Only allow numbers and limit to 10 digits
      const numbersOnly = value.replace(/[^0-9]/g, '');
      const limitedToTen = numbersOnly.slice(0, 10);
      setFormData(prev => ({
        ...prev,
        [name]: limitedToTen
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];

    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setErrors(prev => ({
          ...prev,
          [name]: `File size must be less than 300KB. Current size: ${(file.size / 1024).toFixed(2)}KB`
        }));
        e.target.value = ''; // Reset file input
        return;
      }

      setFormData(prev => ({
        ...prev,
        [name]: file
      }));
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleBarCouncilIdChange = (e) => {
    let value = e.target.value;
    
    // Remove MAH/ if user types it (since it's shown in startAdornment)
    value = value.replace('MAH/', '');

    setFormData(prev => ({
      ...prev,
      barCouncilId: value
    }));

    // Extract year from Bar Council ID (now checking without MAH/ prefix)
    const match = value.match(/\d+\/(\d{4})/);
    if (match) {
      const year = parseInt(match[1]);
      setEnrollmentYear(year);
      setShowCOP(year >= 2011);
    } else {
      setEnrollmentYear(null);
      setShowCOP(false);
    }
  };

  const isFormValid = () => {
    if (activeStep === 0) {
      const newErrors = {};
      if (!formData.name.trim()) newErrors.name = 'Name is required';
      if (!formData.residentialAddress.trim()) newErrors.residentialAddress = 'Residential address is required';
      if (!formData.officeAddress.trim()) newErrors.officeAddress = 'Office address is required';
      
      // Mobile validation
      if (!formData.mobile) {
        newErrors.mobile = 'Mobile number is required';
      } else if (!validateMobile(formData.mobile)) {
        newErrors.mobile = 'Please enter a valid 10-digit mobile number';
      }
      
      // Email validation
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!validateEmail(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }

      if (!isBarCouncilIdValid(formData.barCouncilId)) {
        newErrors.barCouncilId = 'Valid Bar Council ID is required';
      }
      
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    if (activeStep === 1) {
      const newErrors = {};
      if (!formData.photo) newErrors.photo = 'Photo is required';
      if (!formData.barCouncilCertificate) newErrors.barCouncilCertificate = 'Bar Council Certificate is required';
      if (showCOP && !formData.cop) newErrors.cop = 'COP is required for enrollment year 2011 and after';
      if (!formData.paymentProof) newErrors.paymentProof = 'Payment proof is required';
      
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    return true;
  };

  const handleNext = () => {
    if (isFormValid()) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleSubmitClick = () => {
    setOpenConfirmDialog(true);
  };

  const handleConfirmSubmit = () => {
    setOpenConfirmDialog(false);
    handleSubmit();
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleSubmit = async () => {
    if (!isSubmitting) {
      setIsSubmitting(true);
      try {
        // Upload files and get URLs
        const [photoUrl, certificateUrl, copUrl, paymentUrl] = await Promise.all([
          uploadFile(formData.photo, 'photos'),
          uploadFile(formData.barCouncilCertificate, 'certificates'),
          uploadFile(formData.cop, 'cop'),
          uploadFile(formData.paymentProof, 'payments')
        ]);

        // Save application to Firestore
        await addDoc(collection(db, 'membership_applications'), {
          name: formData.name,
          residentialAddress: formData.residentialAddress,
          officeAddress: formData.officeAddress,
          mobile: formData.mobile,
          email: formData.email,
          barCouncilId: `MAH/${formData.barCouncilId}`,
          photoUrl,
          certificateUrl,
          copUrl,
          paymentUrl,
          status: 'pending',
          timestamp: serverTimestamp()
        });

        setSnackbar({
          open: true,
          message: 'Application submitted successfully! We will review and get back to you soon.',
          severity: 'success'
        });

        // Reset form
        setFormData({
          name: '',
          residentialAddress: '',
          officeAddress: '',
          mobile: '',
          email: '',
          barCouncilId: '',
          photo: null,
          barCouncilCertificate: null,
          cop: null,
          paymentProof: null
        });
        setActiveStep(0);

      } catch (error) {
        console.error('Error submitting application:', error);
        setSnackbar({
          open: true,
          message: 'Error submitting application. Please try again.',
          severity: 'error'
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const uploadFile = async (file, path) => {
    if (!file) return null;
    const storageRef = ref(storage, `membership_applications/${path}/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  const isBarCouncilIdValid = (id) => {
    return /^\d+\/\d{4}$/.test(id);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const renderDocumentUpload = (name, label, accept, required = true) => {
    return (
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          {label} {required && '*'}
        </Typography>
        <Box sx={{ mb: 1 }}>
          {formData[name] && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Current file: {formData[name].name}
            </Typography>
          )}
          <input
            accept={accept}
            type="file"
            name={name}
            onChange={handleFileChange}
            style={{ marginBottom: '4px' }}
            disabled={!required && !showCOP}
          />
        </Box>
        {errors[name] && (
          <Typography color="error" variant="caption" display="block">
            {errors[name]}
          </Typography>
        )}
      </Grid>
    );
  };

  const renderReviewStep = () => {
    return (
      <>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h5" component="h2" gutterBottom color="primary" sx={{ mb: 4, fontWeight: 'medium' }}>
            Application Review
          </Typography>

          <Grid container spacing={4}>
            {/* Left Column - Personal Information */}
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: '100%', bgcolor: '#fafafa' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <AccountCircleIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" component="h3" color="primary">
                    Personal Information
                  </Typography>
                </Box>

                {formData.photo && (
                  <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                    <Avatar
                      src={URL.createObjectURL(formData.photo)}
                      alt="Member Photo"
                      sx={{ width: 150, height: 150, border: '3px solid #f5f5f5' }}
                    />
                  </Box>
                )}

                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Name"
                      secondary={formData.name}
                      primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontWeight: 'medium' }}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <PhoneIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Mobile"
                      secondary={formData.mobile}
                      primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontWeight: 'medium' }}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <EmailIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email"
                      secondary={formData.email}
                      primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontWeight: 'medium' }}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <BadgeIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Bar Council ID"
                      secondary={`MAH/${formData.barCouncilId}`}
                      primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontWeight: 'medium' }}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <CalendarTodayIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Enrollment Year"
                      secondary={enrollmentYear}
                      primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontWeight: 'medium' }}
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            {/* Right Column - Addresses and Documents */}
            <Grid item xs={12} md={6}>
              {/* Address Section */}
              <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: '#fafafa' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <LocationOnIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" component="h3" color="primary">
                    Addresses
                  </Typography>
                </Box>

                <List>
                  <ListItem>
                    <ListItemIcon>
                      <LocationOnIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Residential Address"
                      secondary={formData.residentialAddress}
                      primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ 
                        variant: 'body1', 
                        color: 'text.primary',
                        fontWeight: 'medium',
                        style: { whiteSpace: 'pre-wrap' }
                      }}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <LocationOnIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Office Address"
                      secondary={formData.officeAddress}
                      primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ 
                        variant: 'body1', 
                        color: 'text.primary',
                        fontWeight: 'medium',
                        style: { whiteSpace: 'pre-wrap' }
                      }}
                    />
                  </ListItem>
                </List>
              </Paper>

              {/* Documents Section */}
              <Paper elevation={1} sx={{ p: 3, bgcolor: '#fafafa' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <FolderIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" component="h3" color="primary">
                    Uploaded Documents
                  </Typography>
                </Box>

                <List>
                  <ListItem>
                    <ListItemIcon>
                      <InsertDriveFileIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Bar Council Certificate"
                      secondary={formData.barCouncilCertificate?.name || 'Not uploaded'}
                      primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ 
                        variant: 'body1', 
                        color: formData.barCouncilCertificate ? 'text.primary' : 'error.main',
                        fontWeight: 'medium',
                        sx: { 
                          wordBreak: 'break-word',
                          maxWidth: '100%'
                        }
                      }}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <InsertDriveFileIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Certificate of Practice"
                      secondary={parseInt(enrollmentYear) <= 2010 ? 'Not Applicable' : (formData.cop?.name || 'Not uploaded')}
                      primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ 
                        variant: 'body1', 
                        color: parseInt(enrollmentYear) <= 2010 ? 'text.secondary' : (formData.cop ? 'text.primary' : 'error.main'),
                        fontWeight: 'medium',
                        sx: { 
                          wordBreak: 'break-word',
                          maxWidth: '100%'
                        }
                      }}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <InsertDriveFileIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Payment Proof"
                      secondary={formData.paymentProof?.name || 'Not uploaded'}
                      primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ 
                        variant: 'body1', 
                        color: formData.paymentProof ? 'text.primary' : 'error.main',
                        fontWeight: 'medium',
                        sx: { 
                          wordBreak: 'break-word',
                          maxWidth: '100%'
                        }
                      }}
                    />
                  </ListItem>
                </List>
              </Paper>

              <Box sx={{ mt: 4 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Please review all information carefully before submission. You can edit your information using the 'Edit Information' button below.
                </Alert>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={() => setActiveStep(0)}
              startIcon={<EditIcon />}
              sx={{ borderColor: '#580100', color: '#580100' }}
            >
              Edit Information
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitClick}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <SendIcon />}
              sx={{
                bgcolor: '#580100',
                '&:hover': {
                  bgcolor: '#400000'
                }
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          </Box>
        </Paper>

        <Dialog
          open={openConfirmDialog}
          onClose={handleCloseConfirmDialog}
          aria-labelledby="confirm-dialog-title"
          aria-describedby="confirm-dialog-description"
        >
          <DialogTitle id="confirm-dialog-title" sx={{ color: '#580100' }}>
            Confirm Submission
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="confirm-dialog-description">
              Are you sure you want to submit the application? Please review all details carefully before proceeding.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={handleCloseConfirmDialog}
              variant="outlined"
              sx={{ 
                borderColor: '#580100',
                color: '#580100',
                '&:hover': {
                  borderColor: '#400000',
                  color: '#400000'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmSubmit}
              variant="contained"
              sx={{
                bgcolor: '#580100',
                '&:hover': {
                  bgcolor: '#400000'
                }
              }}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Full Name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="residentialAddress"
                label="Residential Address"
                value={formData.residentialAddress}
                onChange={handleInputChange}
                fullWidth
                required
                error={!!errors.residentialAddress}
                helperText={errors.residentialAddress}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="officeAddress"
                label="Office Address"
                value={formData.officeAddress}
                onChange={handleInputChange}
                fullWidth
                required
                error={!!errors.officeAddress}
                helperText={errors.officeAddress}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="mobile"
                label="Mobile Number"
                value={formData.mobile}
                onChange={handleInputChange}
                fullWidth
                required
                error={!!errors.mobile}
                helperText={errors.mobile}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  maxLength: 10
                }}
                placeholder="10-digit mobile number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="email"
                label="Email Address"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                required
                error={!!errors.email}
                helperText={errors.email}
                type="email"
                placeholder="example@domain.com"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="barCouncilId"
                label="Bar Council ID Number"
                value={formData.barCouncilId}
                onChange={handleBarCouncilIdChange}
                fullWidth
                required
                error={!!errors.barCouncilId}
                helperText={errors.barCouncilId || "Format: MAH/XXXX/YYYY (e.g., MAH/4208/2011)"}
                InputProps={{
                  startAdornment: (
                    <Box 
                      component="span" 
                      sx={{ 
                        color: 'text.secondary',
                        userSelect: 'none',
                        mr: 0
                      }}
                    >
                      MAH/
                    </Box>
                  )
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    pl: 0
                  }
                }}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            {renderDocumentUpload('photo', "Member's Photo", 'image/*')}
            {renderDocumentUpload('barCouncilCertificate', 'Bar Council Certificate / SANAD', '.pdf,.jpg,.jpeg,.png')}
            {renderDocumentUpload('cop', 'Certificate of Practice (COP)', '.pdf,.jpg,.jpeg,.png', showCOP)}
            {renderDocumentUpload('paymentProof', 'Payment Proof Screenshot', 'image/*')}
          </Grid>
        );

      case 2:
        return renderReviewStep();

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: 4, mt: 2 }}>
        <Box sx={{ width: '100%', mb: 4 }}>
          <Typography variant="h4" component="h1" align="center" gutterBottom>
            Membership Application
          </Typography>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        {renderStepContent(activeStep)}

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          {activeStep !== 0 && (
            <Button onClick={handleBack}>
              Back
            </Button>
          )}
          {activeStep !== steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{
                bgcolor: '#580100',
                '&:hover': {
                  bgcolor: '#400000'
                }
              }}
            >
              Next
            </Button>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default MembershipApplication;
